import { useCallback, useEffect, useRef, useState } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import VideoPauseOverlayIcon from "@atlaskit/icon/core/video-pause-overlay";
import VideoPlayOverlayIcon from "@atlaskit/icon/core/video-play-overlay";
import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";

export interface VideoProps {
    src: string;
    /**
     * Optional companion source for cross-browser playback. To support
     * transparent (alpha-channel) videos in every modern browser, supply BOTH:
     *
     *   - `src`         → a `.webm` encoded with VP9 + alpha (Chrome/Firefox/Edge)
     *   - `fallbackSrc` → a `.mp4` (or `.mov`) encoded with HEVC (H.265 / hvc1)
     *                     and alpha (Safari, iOS)
     *
     * The component always renders the HEVC source FIRST with
     * `type='video/mp4; codecs="hvc1"'` (so Safari picks it) and the WebM
     * SECOND (so Chrome/Firefox skip the HEVC and use the WebM with alpha).
     * This matches the cross-browser pattern documented at
     * https://supergeekery.com/blog/transparent-video-in-chrome-edge-firefox-and-safari-circa-2022
     *
     * IMPORTANT: A plain H.264 `.mp4` will NOT carry an alpha channel — Safari
     * will play it but the transparency will be opaque/baked-in. The fallback
     * MUST be HEVC for Safari transparency. Either container (`.mp4` or `.mov`)
     * is fine; the codec inside is what matters.
     */
    fallbackSrc?: string;
    alt?: string;
    poster?: string;
    roundedCorners?: boolean;
    desktopHeight?: string;
    tabletHeight?: string;
    mobileHeight?: string;
    disableAutoplay?: boolean;
    showVideoControls?: boolean;
    loop?: boolean;
    playPauseButtonPosition?: "bottom-right" | "bottom-left";
}

const getVideoExtension = (src: string): string =>
    src.split("?")[0]?.split("#")[0]?.split(".").at(-1)?.toLowerCase() ?? "";

/**
 * Returns the `type` attribute for a single-source `<video>` (i.e. when no
 * companion `fallbackSrc` is provided). When a fallback IS provided, the
 * source list is hard-coded to `[HEVC mp4, webm]` and this helper is not used.
 */
const getSingleSourceType = (src: string): string => {
    const ext = getVideoExtension(src);
    if (ext === "webm") return "video/webm";
    return "video/mp4";
};

export const Video = ({
    src,
    fallbackSrc,
    alt = "",
    poster,
    roundedCorners = false,
    desktopHeight,
    tabletHeight,
    mobileHeight,
    disableAutoplay = false,
    showVideoControls = false,
    loop = false,
    playPauseButtonPosition = "bottom-right"
}: // eslint-disable-next-line complexity
VideoProps) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [hasError, setHasError] = useState(false);

    // Reset the error state whenever the source list changes. This replaces the
    // old `onLoadStart={() => setHasError(false)}` handler, which fired on
    // every `<source>` the browser tried — racing with `onError` events from
    // earlier `<source>` elements (e.g. a codec rejection on the first source)
    // and intermittently leaving `hasError` stuck `true` between refreshes.
    useEffect(() => {
        setHasError(false);
    }, [src, fallbackSrc]);

    // Play from beginning when in viewport, pause when out.
    // Note: we intentionally do NOT depend on `hasError` here. A `<source>`-level
    // error must not prevent the play attempt — the browser will fall through to
    // the next source on its own. We still call `play()`; the returned promise
    // simply rejects if there's truly nothing to play, which we swallow.
    useEffect(() => {
        const video = videoRef.current;
        if (!video || disableAutoplay) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry && entry.isIntersecting) {
                    video.currentTime = 0; // Always start from beginning
                    video.play().catch(() => {
                        // Ignore play errors (autoplay restrictions, power
                        // saving, no decodable source, etc.)
                    });
                } else {
                    video.pause();
                }
            },
            { threshold: 0.5 }
        );

        observer.observe(video);

        return () => {
            observer.disconnect();
        };
    }, [disableAutoplay]);

    const { createAnalyticsEvent } = useAnalyticsEvents();

    const togglePlay = useCallback(() => {
        if (videoRef.current && !hasError) {
            if (isPlaying) {
                videoRef.current.pause();
                setIsPlaying(false);
                createAnalyticsEvent({
                    eventType: "ui",
                    action: "clicked",
                    actionSubject: "button",
                    actionSubjectId: "pauseVideo",
                    source: "videoContainer",
                    attributes: { label: "pause" }
                }).fire();
            } else {
                videoRef.current
                    .play()
                    .then(() => {
                        setIsPlaying(true);
                        createAnalyticsEvent({
                            eventType: "ui",
                            action: "clicked",
                            actionSubject: "button",
                            actionSubjectId: "playVideo",
                            source: "videoContainer",
                            attributes: { label: "play" }
                        }).fire();
                    })
                    .catch(() => {
                        // Ignore error likely due to autoplay restrictions or browser power saving
                        setIsPlaying(false);
                    });
            }
        }
    }, [videoRef, hasError, isPlaying, createAnalyticsEvent]);

    const videoCss = css({
        width: "100%",
        [media.above.md]: {
            height: desktopHeight ? desktopHeight : "auto"
        },
        [media.only.sm]: {
            height: tabletHeight ? tabletHeight : "auto"
        },
        [media.below.sm]: {
            height: mobileHeight ? mobileHeight : "auto"
        }
    });

    const intl = useIntl();

    // When BOTH a primary and a fallback source are provided, the contract is:
    //   `src`         = .webm (VP9 + alpha)
    //   `fallbackSrc` = .mp4/.mov (HEVC / hvc1 + alpha)
    // The HEVC source MUST be rendered FIRST with `codecs="hvc1"` so Safari
    // picks it (Safari supports WebM but not WebM-with-alpha; without this
    // ordering Safari renders the alpha as a black background). Chrome/Firefox
    // do not support hvc1 and will skip past it to the WebM.
    // See: https://supergeekery.com/blog/transparent-video-in-chrome-edge-firefox-and-safari-circa-2022
    const sources: { src: string; type: string }[] = fallbackSrc
        ? [
              { src: fallbackSrc, type: 'video/mp4; codecs="hvc1"' },
              { src, type: "video/webm" }
          ]
        : [{ src, type: getSingleSourceType(src) }];

    return (
        <div
            css={[
                css({ position: "relative" }),
                roundedCorners && roundedCornersCss
            ]}
        >
            <video
                ref={videoRef}
                playsInline
                muted={!disableAutoplay && !showVideoControls}
                preload="auto"
                poster={poster}
                controls={showVideoControls}
                loop={loop}
                css={videoCss}
                style={{
                    maxWidth: "100%"
                }}
                aria-label={alt}
                aria-hidden={alt === ""}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onEnded={() => setIsPlaying(false)}
                // Only mark the component as errored when the browser has
                // exhausted ALL `<source>` elements (NETWORK_NO_SOURCE). A
                // single source-level rejection (e.g. codec mismatch on the
                // HEVC fallback when the asset is actually H.264) is not a
                // failure — the browser will fall through to the next source.
                onError={() => {
                    const v = videoRef.current;
                    if (
                        v &&
                        v.networkState === HTMLMediaElement.NETWORK_NO_SOURCE
                    ) {
                        setHasError(true);
                    }
                }}
            >
                {sources.map((source) => (
                    <source
                        key={source.src}
                        src={source.src}
                        type={source.type}
                    />
                ))}
                <track kind="captions" />
                Video not supported.
            </video>
            {!hasError && !showVideoControls && (
                <button
                    className={playPauseButtonPosition}
                    css={controlButtonCss}
                    onClick={togglePlay}
                >
                    <div css={buttonCircleCss}>
                        {isPlaying ? (
                            <VideoPauseOverlayIcon
                                label={intl.formatMessage({
                                    id: "video.pause",
                                    defaultMessage: "Pause"
                                })}
                            />
                        ) : (
                            <VideoPlayOverlayIcon
                                label={intl.formatMessage({
                                    id: "video.play",
                                    defaultMessage: "Play"
                                })}
                            />
                        )}
                    </div>
                </button>
            )}
        </div>
    );
};

const controlButtonCss = css({
    "position": "absolute",
    "insetBlockEnd": token("space.100"),
    "padding": token("space.075"),
    "height": "44px",
    "width": "44px",
    "backgroundColor": "transparent",
    "border": "none",
    "borderRadius": "100%",

    "&.bottom-left": {
        insetInlineStart: token("space.100")
    },
    "&.bottom-right": {
        insetInlineEnd: token("space.100")
    },
    ["&:hover"]: {
        cursor: "pointer"
    }
});

const buttonCircleCss = css({
    height: "32px",
    width: "32px",
    backgroundColor: wacToken("color.neutral.light400"),
    border: "none",
    borderRadius: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",

    ["&:hover"]: {
        cursor: "pointer",
        backgroundColor: wacToken("color.neutral.light500")
    },

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    span: {
        color: `${wacToken("color.neutral.light0")} !important`
    }
});

const roundedCornersCss = css({
    borderRadius: wacToken("corner.radius.card"),
    overflow: "hidden"
});
